import { bulkManualSchedulerUtils } from '@/pages/KInterviewScheduler/BulkManualScheduler/bulkManualScheduler.utils';
import { KText } from '@/ui-library/components';
import { Flex, Icon } from '@chakra-ui/react';
import { InfoCircle } from '@kula-ai/icons/dual-tone/InfoCircle';
import React, { useEffect, useState } from 'react';

const { isConflictingWithBulkBookedEvents } = bulkManualSchedulerUtils;

// Function to calculate conflict status purely
const calculateConflictStatus = (interviewData, isBulk) => {
  if (!isBulk) return false;
  const { bookedSlot, bulkBookedEvents } = interviewData ?? {};
  const { start, end, timezone } = bookedSlot ?? {};
  return isConflictingWithBulkBookedEvents(bulkBookedEvents, {
    start,
    end,
    timezone,
  });
};

export const SchBulkConflictError = ({
  formik,
  formRef,
  isBulkManual,
  interviewDataRef,
  registerForUpdates,
}) => {
  // Initialize state based on initial data (might be incomplete)
  const [isConflictingState, setIsConflicting] = useState(() =>
    calculateConflictStatus(interviewDataRef.current, isBulkManual)
  );

  useEffect(() => {
    if (!isBulkManual) return;

    const unregister = registerForUpdates('bookedSlot', (data) => {
      const { fullData } = data ?? {};
      const { setFieldValue, values } = formik ?? {};
      const newIsConflicting = calculateConflictStatus(fullData, isBulkManual);

      setIsConflicting((prev) =>
        prev === newIsConflicting ? prev : newIsConflicting
      );

      if (
        isConflictingState &&
        setFieldValue &&
        values?.showDisplayMode !== 'dates'
      ) {
        setFieldValue('showDisplayMode', 'dates');
      }
    });

    return () => {
      unregister();
    };
  }, [isBulkManual, registerForUpdates, formik, isConflictingState]);

  useEffect(() => {
    const { setFieldValue, values, setTouched } = formik ?? {};
    if (
      isConflictingState &&
      setFieldValue &&
      values?.showDisplayMode !== 'dates'
    ) {
      setFieldValue('showDisplayMode', 'dates');
      setTouched({
        startTime: true,
        endTime: true,
      });
    }
    // This effect runs whenever isConflictingState changes or formRef instance changes.
  }, [isConflictingState, formik]);

  useEffect(() => {
    if (isConflictingState) {
      const id = 'sch-bulk-conflict-error';
      // Use timeout to ensure element exists after render
      const timerId = setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 0);

      return () => clearTimeout(timerId);
    }
  }, [isConflictingState]);

  if (!isConflictingState) return null;

  const id = 'sch-bulk-conflict-error';

  return (
    <Flex id={id} gap="6px" alignItems="start" mt="-6px">
      <Icon color="error-100" as={InfoCircle} mt="3px" />
      <KText color="error-200">
        Another interview is booked at this slot. Select an available time.
      </KText>
    </Flex>
  );
};
